import { createSelector } from '@reduxjs/toolkit';

import {
  filterTableData,
  getUniqueTags,
  getUniqueValues,
  sortTableData,
} from '../../misc/functions';
import { COLUMNS } from '../../misc/columns.constants';
import { DESC } from './table.slice';
import type { RootState } from '../../store/store';

const selectWebsites = (state: RootState) =>
  state.table.websitesData?.websites ?? [];
const selectFilters = (state: RootState) => state.table.filters;
const selectTags = (state: RootState) => state.table.tags;
const selectSort = (state: RootState) => state.table.sort;

// Websites passing the active column filters + tag include/exclude rules.
export const selectFilteredWebsites = createSelector(
  [selectWebsites, selectFilters, selectTags],
  (websites, filters, tags) => filterTableData([...websites], filters, tags),
);

// Filtered websites, sorted by the active sort column/direction.
export const selectPreparedData = createSelector(
  [selectFilteredWebsites, selectSort],
  (filtered, sort) => {
    const sorted = sortTableData(filtered, sort.column || COLUMNS.website);
    return sort.direction === DESC ? sorted.reverse() : sorted;
  },
);

// Tags present in the currently filtered set (drives which tags are selectable).
export const selectAvailableTags = createSelector(
  [selectFilteredWebsites],
  (filtered) => getUniqueTags(filtered),
);

// Per-filter autocomplete suggestions derived from the filtered set.
export const selectAutocompleteLists = createSelector(
  [selectFilteredWebsites, selectFilters],
  (filtered, filters) => {
    const lists: Record<string, string[]> = {};
    Object.keys(filters).forEach((name) => {
      lists[name] = getUniqueValues(filtered, name).sort();
    });
    return lists;
  },
);
